import { Box, Button, FileInput, FormField, RangeInput, Select, Tab, Tabs, Text, TextInput } from "grommet"
import { Bold, Close, FormDown, FormUp, Italic, TextAlignCenter, TextAlignLeft, TextAlignRight, Underline } from "grommet-icons";
import ButtonGroup from "../../../forms/ButtonGroup";
import ShadowSettingsForm from "./sections/ShadowSettingsForm";
import { SettingType } from "./utils/SettingType";
import { TextShadow } from "../../../icons/TextShadow";
import { Fonts } from "../../../icons/Fonts";
import { FontOutline } from "../../../icons/FontOutline";
import { CircleHalfFull } from "../../../icons/CircleHalfFull";
import { MoveResize } from "../../../icons/MoveResize";
import RangeInputWithEdit from "../../../forms/RangeInputWithEdit";
import { rouletteItemTextAlignment, rouletteItemTextStyle, rouletteItemTextTransformation } from "../RouletteConstants";
import { useEffect, useRef, useState } from "react";
import '../../../forms/Form.css'
import { useTranslation } from "react-i18next";
import { useGetMediasByCategoryQuery } from "../../../api/mediasApi";
import { loadFontStylesheets } from "../../wheel-customization/fontMediaUtils";

const textStylesOptions = [
    { icon: <Bold/>, value: rouletteItemTextStyle.BOLD },
    { icon: <Italic/>, value: rouletteItemTextStyle.ITALIC },
    { icon: <Underline/>, value: rouletteItemTextStyle.UNDERLINE },
]

const transformationOptions = [
    { labelKey: 'optionNone', icon: <Close/>, value: rouletteItemTextTransformation.NONE },
    { labelKey: 'optionUppercase', icon: <FormUp/>, value: rouletteItemTextTransformation.UPPERCASE },
    { labelKey: 'optionLowercase', icon: <FormDown/>, value: rouletteItemTextTransformation.LOWERCASE },
]

const alignmentOptions = [
    { labelKey: 'optionLeft', icon: <TextAlignLeft/>, value: rouletteItemTextAlignment.LEFT },
    { labelKey: 'optionCenter', icon: <TextAlignCenter/>, value: rouletteItemTextAlignment.CENTER },
    { labelKey: 'optionRight', icon: <TextAlignRight/>, value: rouletteItemTextAlignment.RIGHT },
]

const RouletteItemTextSettings = () => {

    const { t } = useTranslation('roulette');

    const { data: medias, isSuccess: isMediaLoaded } = useGetMediasByCategoryQuery({ categoryCode: 'roulette_category_font' });
    const [fonts, setFonts] = useState([]);

    const [textStyles, setTextValues] = useState(textStylesOptions);

    useEffect(() => {
        if (isMediaLoaded) {
            loadFontStylesheets(medias);
            setFonts(medias);
        }
    }, [medias, isMediaLoaded])

    return (
        <Box>
            <Tabs flex>
                <Tab title={t('fontTabTitle')} icon={<Fonts/>}>
                    <Box background={'background-contrast'} pad={'small'} round={'small'} margin={{ top: 'medium' }}>
                        <FormField label={t('fontLabel')}>
                            <Select 
                                name="font" 
                                options={fonts} 
                                labelKey={'name'} 
                                // valueKey={{ key: 'name', reduce: true }}
                                valueLabel={(option) => <Text style={{ padding: '6px 12px', fontFamily: option.name }}>{option.name}</Text>}
                                children={(option) => (
                                    <Box pad="small">
                                        <Text style={{ fontFamily: `"${option.name}", sans-serif` }}>
                                            {option.name}
                                        </Text>
                                    </Box>
                                )}
                            />
                        </FormField>
                        <FormField label={t('textColorLabel')}>
                            <TextInput name="textColor" type="color" />
                        </FormField>
                        <FormField 
                            label={t('textStyleLabel')}
                            name="textStyle"
                            className="no-border-form-field"
                            // value={textStyles}
                            // onChange={handleTextStyle}
                            component={(props) => <ButtonGroup value={textStyles} onChange={props.onChange} defaultValue={props.value}/>}
                        ></FormField>
                        <FormField label={t('transformationLabel')}>
                            <Select 
                                name="textTransformation"
                                options={transformationOptions} 
                                labelKey={(option) => <SettingType option={{ ...option, label: t(option.labelKey) }} />}
                                valueKey={{ key: 'value', reduce: true }}
                            />
                        </FormField>
                        <FormField label={t('alignmentLabel')}>
                            <Select 
                                name="textAlignment"
                                options={alignmentOptions} 
                                labelKey={(option) => <SettingType option={{ ...option, label: t(option.labelKey) }} />}
                                valueKey={{ key: 'value', reduce: true }}
                            />
                        </FormField>
                    </Box>
                </Tab>
                <Tab title={t('textTabTitle')} icon={<FontOutline />}>
                    <Box background={'background-contrast'} pad={'small'} round={'small'} margin={{ top: 'medium' }}>
                        <FormField label={t('fontSizeLabel')} noBorder noFocus>
                            <RangeInputWithEdit name="fontSize" type="number" unit={'px'} min={8} max={40} step={1} />
                        </FormField>
                        <FormField label={t('lineHeightLabel')} noBorder noFocus>
                            <RangeInputWithEdit name="lineHeight" type="number" unit={'u'} min={0} max={5} step={0.01} />
                        </FormField>
                        <FormField label={t('letterSpacingLabel')} noBorder noFocus>
                            <RangeInputWithEdit name="letterSpacing" type="number" unit={'px'} min={-2} max={3} step={0.01} />
                        </FormField>
                        <FormField label={t('wordSpacingLabel')} noBorder noFocus>
                            <RangeInputWithEdit name="wordSpacing" type="number" unit={'px'} min={0} max={8} step={0.01} />
                        </FormField>
                    </Box>
                </Tab>
                <Tab title={t('shadowTabTitle')} icon={<TextShadow/>}>
                    <ShadowSettingsForm
                        shadowColorInputName="textShadowColor"
                        shadowHorizontalOffsetInputName="textShadowHorizontalOffset"
                        shadowVerticalOffsetInputName="textShadowVerticalOffset"
                        shadowBlurInputName="textShadowBlur"
                    />
                </Tab>
                <Tab title={t('outlineTabTitle')} icon={<CircleHalfFull/>}>
                    <Box background={'background-contrast'} pad={'small'} round={'small'} margin={{ top: 'medium' }}>
                        <FormField label={t('outlineWidthLabel')} noBorder noFocus>
                            <RangeInputWithEdit name="fontOutlineWidth" type="number" unit={'px'} min={0} max={4} step={0.01} />
                        </FormField>
                        <FormField label={t('outlineColorLabel')}>
                            <TextInput name="textOutlineColor" type="color" />
                        </FormField>
                        <FormField label={t('textOutlineBackgroundSizeLabel')} noBorder noFocus>
                            <RangeInputWithEdit name="textOutlineBackgroundSize" type="number" unit={'px'} min={0} max={10} step={0.01} />
                        </FormField>
                        <FormField label={t('textOutlineBackgroundColorLabel')}>
                            <TextInput name="textOutlineBackgroundColor" type="color" />
                        </FormField>
                    </Box>
                </Tab>
                <Tab title={t('textDemensionsTabTitle')} icon={<MoveResize/>}>
                    <Box background={'background-contrast'} pad={'small'} round={'small'} margin={{ top: 'medium' }}>
                        <FormField label={t('blockWidthLabel')} noBorder noFocus>
                            <RangeInputWithEdit name="fontBlockWidth" type="number" unit={'px'} min={10} max={200} step={1} />
                        </FormField>
                        <FormField label={t('positionXLabel')} noBorder noFocus>
                            <RangeInputWithEdit name="fontPositionX" type="number" unit={'px'} min={-100} max={100} step={0.01} />
                        </FormField>
                        <FormField label={t('positionYLabel')} noBorder noFocus>
                            <RangeInputWithEdit name="fontPositionY" type="number" unit={'px'} min={-100} max={100} step={0.01} />
                        </FormField>
                    </Box>
                </Tab>
            </Tabs>
        </Box>
    )
}

export default RouletteItemTextSettings;
